import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../redux/store';

/**
 * Events wal-backend-v2 broadcasts into the `order_<masterOrderId>` room that mean
 * "this order's data changed, re-read it":
 *  - `customer-request-processed` — ops approved / rejected / dismissed a customer
 *    request (edit, cancellation, update_location) from the WAL order detail page.
 *    See api/services/OrderCustomerRequestService.js -> broadcastRequestProcessed.
 *  - `order-location-updated` — a location row was written for this order, which is
 *    what ops does right after approving a "request location update".
 *    See api/models/OrderLocationUpdate.js afterCreate/afterUpdate.
 *
 * Both payloads are ID-only by design — the portal re-fetches through its own API,
 * which re-applies this customer's authorization. Never render them directly.
 */
const ORDER_LIVE_SYNC_EVENTS = ['customer-request-processed', 'order-location-updated'] as const;

interface IOrderLiveSyncEvent {
    masterOrderId?: number | string;
    orderLegId?: number | null;
    requestId?: number;
    requestType?: string;
    editType?: string | null;
    status?: string;
    orderLocationUpdateId?: number;
}

/**
 * Approving an edit request fires several writes in a row (the request row, the leg,
 * then a location ping on some flows), so events arrive in bursts. Coalesce them into
 * a single re-fetch instead of hammering the detail endpoint once per event.
 */
const REFETCH_COALESCE_MS = 800;

/**
 * Keep an order detail page in sync with changes made on the WAL side, without a hard refresh.
 *
 * Joins the order's socket room and calls `onRefresh` (coalesced) whenever WAL reports
 * the order changed. The socket is WAL's — the portal's connection already points there
 * (see connectSocket in redux/basic.slice.tsx), so no extra connection is opened.
 *
 * @param orderId   masterOrderId (orders.id) — the portal and WAL use the same id
 * @param onRefresh called on the trailing edge of an event burst; re-fetch here
 */
export const useOrderLiveSync = (orderId?: string | number | null, onRefresh?: (event: IOrderLiveSyncEvent) => void) => {
    const { socketIo, isSocketConnected } = useSelector((state: RootState) => state.basic);

    // Held in a ref so a new callback identity on every render doesn't tear down and
    // re-join the room — the effect must depend only on the socket and the order.
    const onRefreshRef = useRef(onRefresh);
    onRefreshRef.current = onRefresh;

    useEffect(() => {
        if (!socketIo || !isSocketConnected || !orderId) {
            return;
        }

        const roomName = `order_${orderId}`;
        let coalesceTimer: ReturnType<typeof setTimeout> | null = null;

        socketIo.get(`/subscribe/${roomName}`, (_body: any, jwr: any) => {
            if (jwr?.error) {
                console.error(`useOrderLiveSync: could not join ${roomName}`, jwr.error);
            }
        });

        const handleOrderChanged = (data: IOrderLiveSyncEvent) => {
            // The room is already per-order, but the socket never LEAVES a room (sails
            // exposes no client-side leave here), so after navigating between orders the
            // same connection sits in several order rooms. Drop anything that isn't the
            // order currently on screen.
            if (data?.masterOrderId != null && `${data.masterOrderId}` !== `${orderId}`) {
                return;
            }

            if (coalesceTimer) {
                clearTimeout(coalesceTimer);
            }

            coalesceTimer = setTimeout(() => {
                coalesceTimer = null;
                onRefreshRef.current?.(data);
            }, REFETCH_COALESCE_MS);
        };

        ORDER_LIVE_SYNC_EVENTS.forEach((eventName) => socketIo.on(eventName, handleOrderChanged));

        return () => {
            if (coalesceTimer) {
                clearTimeout(coalesceTimer);
            }
            ORDER_LIVE_SYNC_EVENTS.forEach((eventName) => socketIo.off(eventName, handleOrderChanged));
        };
    }, [socketIo, isSocketConnected, orderId]);
};

export default useOrderLiveSync;
